import { useEffect, useState } from 'react'
import dynamic from 'next/dynamic'
import Link from 'next/link'
import { useDispatch } from 'react-redux'
import styled, { useTheme } from 'styled-components'
import { ProductCategoryScheme, ReviewImageScheme, ReviewScheme, SupplyTypeEnum } from '@/apis/rest/generated/types'
import FlatIcon from '@/components/common/icons/FlatIcon'
import { Image } from '@/components/common/images/Image'
import Modal from '@/components/common/modals/Modal'
import { Typo, TypoVariant } from '@/components/common/typography'
import ModalReviewImageFullScreenV2 from '@/components/domains/modals/ModalReviewImageFullScreenV2'
import { IconNameEnum } from '@/constants/icon-name.enum'
import { S3_HOST_URL } from '@/constants/legacy/constEnv'
import ROUTES from '@/constants/legacy/constRoutes'
import { ToastConst } from '@/constants/toast.const'
import { useProductDetailEventTracker } from '@/containers/event-tracker/product-detail.event-tracker-new'
import { useDebounceCallback } from '@/containers/hooks/common/useDebounceCallback.hook'
import { useReviewToggleLikedMutation } from '@/containers/hooks/reviews/useToggleReviewLikedMutation.hook'
import { doSetFullScreenReviewImagesV2, doSetGlobalToastContent, doSetToggleModalLogin } from '@/stores/reduxUI'

const NoSSRReviewDate = dynamic(() => import('./ReviewDate'), { ssr: false })

interface ProductDetailReviewsProps {
  isUserLoggedIn: boolean
  reviews: ReviewScheme[]
  reviewCount: number
  reviewScore: number
  brandId: number
  brandName: string
  productId: number
  productName: string
  isRealSoldOut: boolean
  price: number
  discountRate: number
  salePrice: number
  isTimeSale: boolean
  categories: ProductCategoryScheme[]
  supplyType: SupplyTypeEnum
}

export const ProductDetailReviews = ({
  isUserLoggedIn,
  reviews,
  reviewCount,
  reviewScore,
  brandId,
  brandName,
  productId,
  productName,
  isRealSoldOut,
  price,
  discountRate,
  salePrice,
  isTimeSale,
  categories,
  supplyType,
}: ProductDetailReviewsProps) => {
  const { color } = useTheme()
  const { trackClickProductDetailMoreReviewButton } = useProductDetailEventTracker()
  const dispatch = useDispatch()
  const { toggleReviewLiked } = useReviewToggleLikedMutation()
  const [reviewList, setReviewList] = useState<ReviewScheme[]>(reviews)
  const [isShowReportModal, setIsShowReportModal] = useState(false)

  /**
   * TODO: ReviewList에서는 props만 reviews를 사용하고 ReviewItem을 만들어서 state를 사용해야 됨
   * 현재 reviews props가 갱신되어도 반영이 안됨
   * useEffect를 사용해서 reviews가 변경될 때마다 reviewList를 업데이트 해줘야 함
   */
  useEffect(() => {
    setReviewList(reviews)
  }, [reviews])

  const handleClickReport = () => {
    if (!isUserLoggedIn) {
      dispatch(doSetToggleModalLogin(true))
      return
    }
    setIsShowReportModal(true)
  }

  const handleClickReviewToggleLike = async ({ reviewId }: { reviewId: number }) => {
    if (!isUserLoggedIn) {
      dispatch(doSetToggleModalLogin(true))
      return
    }
    const result = await toggleReviewLiked(reviewId)
    if (result) {
      const updatedReviewList = reviewList.map((review) => {
        if (review.id === reviewId) {
          return {
            ...review,
            likedCount: Number(result.likedCount),
            isLiked: Boolean(result.isLiked),
          }
        }
        return review
      })
      setReviewList(updatedReviewList)
    }
  }

  const { debouncedFunction } = useDebounceCallback({
    callback: handleClickReviewToggleLike,
    delay: 300,
  })

  const handleClickReviewImage = ({ photos, index }: { photos: ReviewImageScheme[]; index: number }) => {
    dispatch(
      doSetFullScreenReviewImagesV2({
        reviewImages: photos.map((image) => `${S3_HOST_URL}/${image.path}`),
        showIndex: index,
      })
    )
    // 뒤로가기(웹 popstate / 앱 백키 → history.go)가 페이지 이동이 아닌 모달 닫기로 동작하도록
    // 히스토리 엔트리를 하나 쌓는다.
    window.history.pushState({ reviewImageFullScreenV2: true }, '')
  }

  const handleClickMoreReview = () => {
    trackClickProductDetailMoreReviewButton({
      brandId,
      brandName,
      productId,
      productName,
      isRealSoldOut,
      price,
      discountRate,
      salePrice,
      reviewScore,
      reviewCount,
      isTimeSale,
      categories,
      supplyType,
    })
  }

  const handleClickReportCancel = () => {
    setIsShowReportModal(false)
  }

  const handleClickReportConfirm = () => {
    setIsShowReportModal(false)
    dispatch(
      doSetGlobalToastContent({
        content: '신고가 완료되었습니다.',
        duration: 2000,
        variant: ToastConst.ToastVariant.withCTA,
      })
    )
  }

  const isEmptyReviews = reviewList.length === 0

  return (
    <ProductDetailReviewsWrapper>
      <div className="product-detail-reviews">
        <div className="product-detail-reviews__title">
          <Typo as="h2" variant={TypoVariant.Heading6Bold} color={color.gray900}>
            상품 리뷰
          </Typo>
        </div>
        {isEmptyReviews ? (
          <div className="product-detail-reviews__empty">
            <FlatIcon type={IconNameEnum.IcEmpty} size="6.4rem" />
            <Typo variant={TypoVariant.Body3SemiBold} color={color.gray600}>
              아직 작성된 리뷰가 없어요
            </Typo>
          </div>
        ) : (
          <>
            <div className="product-detail-reviews__summary">
              <div className="product-detail-reviews__star">
                {Array.from({ length: 5 }).map((_, index) => (
                  <FlatIcon
                    key={index}
                    type={IconNameEnum.IcStar}
                    color={index + 1 <= reviewScore ? color.yellow500 : color.gray100}
                    size="1.8rem"
                  />
                ))}
              </div>
              <div className="product-detail-reviews__score">
                <Typo variant={TypoVariant.Heading6Bold} color={color.gray900}>
                  {reviewScore}
                </Typo>
              </div>
              <div className="product-detail-reviews__count">
                <Typo variant={TypoVariant.Body1Regular} color={color.gray400}>
                  ({reviewCount.toLocaleString('ko-KR')})
                </Typo>
              </div>
            </div>
            <ul className="product-detail-reviews__review-list">
              {reviewList.map((review, index) => {
                return (
                  <li key={review.id} className="product-detail-reviews__review-item">
                    <div className="product-detail-reviews__review-header">
                      <div className="product-detail-reviews__review-user-info">
                        <div className="product-detail-reviews__review-user-info-top">
                          <div className="product-detail-reviews__review-star">
                            {Array.from({ length: 5 }).map((_, index) => (
                              <FlatIcon
                                key={index}
                                type={IconNameEnum.IcStar}
                                color={index + 1 <= review.score ? color.yellow500 : color.gray100}
                                size="1.6rem"
                              />
                            ))}
                          </div>
                          <div className="product-detail-reviews__review-date" suppressHydrationWarning={true}>
                            <NoSSRReviewDate time={review.createdAt || ''} />
                          </div>
                        </div>
                        <div className="product-detail-reviews__review-username">
                          <Typo variant={TypoVariant.Body4Regular} color={color.gray400}>
                            {review.username}
                          </Typo>
                        </div>
                      </div>
                      <button
                        className="product-detail-reviews__review-report"
                        type="button"
                        onClick={handleClickReport}
                      >
                        <Typo variant={TypoVariant.Body5Regular} color={color.gray400}>
                          신고하기
                        </Typo>
                      </button>
                    </div>
                    <div className="product-detail-reviews__review-product">
                      <Typo variant={TypoVariant.Body4Regular} color={color.gray400}>
                        구매한 상품
                      </Typo>
                      <Typo variant={TypoVariant.Body4Regular} color={color.gray700}>
                        {review.productOptionName}
                      </Typo>
                    </div>
                    <div className="product-detail-reviews__review-content">
                      <div className="product-detail-reviews__review-text">
                        <Typo variant={TypoVariant.Body3Regular} color={color.gray800}>
                          {review.reviewBody}
                        </Typo>
                      </div>
                      <div className="product-detail-reviews__review-images">
                        {review.photos.map((image, index) => (
                          <div
                            key={image.id}
                            className="product-detail-reviews__review-image"
                            onClick={() =>
                              handleClickReviewImage({
                                photos: review.photos,
                                index,
                              })
                            }
                          >
                            <Image
                              src={image.thumbnailPath ? `${S3_HOST_URL}/${image.thumbnailPath}` : ''}
                              alt={`review-image-${index + 1}`}
                            />
                          </div>
                        ))}
                      </div>
                    </div>
                    <button
                      className={`product-detail-reviews__review-like-button ${
                        review.isLiked && 'product-detail-reviews__review-like-button--active'
                      }`}
                      type="button"
                      onClick={() => debouncedFunction({ reviewId: review.id })}
                    >
                      {review.isLiked ? (
                        <>
                          <Typo variant={TypoVariant.Body3Bold} color={color.blue500}>
                            도움됨
                          </Typo>
                          <Typo variant={TypoVariant.Body3Bold} color={color.blue500}>
                            {review.likedCount}
                          </Typo>
                        </>
                      ) : (
                        <>
                          <Typo variant={TypoVariant.Body3Medium} color={color.gray900}>
                            도움이 됐어요
                          </Typo>
                          <Typo variant={TypoVariant.Body3Medium} color={color.gray500}>
                            {review.likedCount}
                          </Typo>
                        </>
                      )}
                    </button>
                  </li>
                )
              })}
            </ul>
            <div className="product-detail-reviews__more">
              {reviews.length > 0 && (
                <Link
                  href={{
                    pathname: ROUTES.PRODUCTS.REVIEWS,
                    query: { productId },
                  }}
                >
                  <button type="button" className="product-detail-reviews__more-button" onClick={handleClickMoreReview}>
                    <Typo variant={TypoVariant.Body1Medium} color={color.gray900}>
                      더 많은 리뷰 보기
                    </Typo>
                    <FlatIcon type={IconNameEnum.IcChevronRight} color={color.gray900} size="2rem" />
                  </button>
                </Link>
              )}
            </div>
            <Modal open={isShowReportModal}>
              <Modal.Content>
                <Modal.Title>
                  <Typo variant={TypoVariant.Heading6Bold} color={color.gray900}>
                    해당 리뷰를 신고하시겠어요?
                  </Typo>
                </Modal.Title>
              </Modal.Content>
              <Modal.Footer style={{ display: 'flex', alignItems: 'center', gap: '0.4rem' }}>
                <ModalButton className="cancel" onClick={handleClickReportCancel}>
                  <Typo variant={TypoVariant.Body1Medium} color={color.gray900}>
                    취소
                  </Typo>
                </ModalButton>

                <ModalButton className="ok" onClick={handleClickReportConfirm}>
                  <Typo variant={TypoVariant.Body1Bold} color={color.grayWhite}>
                    확인
                  </Typo>
                </ModalButton>
              </Modal.Footer>
            </Modal>
          </>
        )}
      </div>
      <ModalReviewImageFullScreenV2 />
    </ProductDetailReviewsWrapper>
  )
}

const ProductDetailReviewsWrapper = styled.section`
  .product-detail-reviews {
    padding: 1.6rem;
    &__empty {
      margin-top: 1.6rem;
      display: flex;
      flex-direction: column;
      align-items: center;
      justify-content: center;
      gap: 1.2rem;
    }
    &__summary {
      margin-top: 1.6rem;
      padding: 1.6rem;
      border-radius: 0.8rem;
      border: 1px solid
        ${({
          theme: {
            color: { gray100 },
          },
        }) => gray100};
      background-color: ${({
        theme: {
          color: { grayWhite },
        },
      }) => grayWhite};
      display: flex;
      flex-direction: row;
      align-items: center;
      justify-content: center;
    }
    &__star {
      display: flex;
      flex-direction: row;
      gap: 0.2rem;
      align-items: center;
    }
    &__score {
      margin-left: 0.8rem;
    }
    &__count {
      margin-left: 0.4rem;
    }
    &__review-list {
      margin: 1.6rem 0 0;
    }
    &__review-item {
      padding: 1.6rem 0;
      border-radius: 0.8rem;
      border-bottom: 0.1rem solid
        ${({
          theme: {
            color: { gray70 },
          },
        }) => gray70};
      background-color: ${({
        theme: {
          color: { grayWhite },
        },
      }) => grayWhite};
      &:last-child {
        border-bottom: none;
      }
    }
    &__review-header {
      display: flex;
      flex-direction: row;
      justify-content: space-between;
      align-items: center;
    }
    &__review-user-info {
      display: flex;
      flex-direction: column;
    }
    &__review-user-info-top {
      display: flex;
      flex-direction: row;
      align-items: center;
      gap: 0.6rem;
    }
    &__review-star {
      display: flex;
      flex-direction: row;
      gap: 0.2rem;
      align-items: center;
    }
    &__review-report {
      cursor: pointer;
      padding: 0.8rem;
      border: none;
      background-color: transparent;
    }
    &__review-product {
      display: flex;
      flex-direction: row;
      align-items: center;
      gap: 0.8rem;
      margin-top: 0.8rem;
      padding: 1.2rem;
      border-radius: 0.8rem;
      background-color: ${({
        theme: {
          color: { gray50 },
        },
      }) => gray50};
      > div:first-child {
        flex-shrink: 0;
      }
      > div:last-child {
        width: 100%;
        text-overflow: ellipsis;
        overflow: hidden;
        white-space: nowrap;
      }
    }
    &__review-content {
      margin-top: 1.2rem;
      white-space: pre-line;
    }
    &__review-images {
      cursor: pointer;
      margin-top: 1.2rem;
      display: flex;
      flex-direction: row;
      gap: 0.4rem;
    }
    &__review-image {
      border-radius: 0.8rem;
      overflow: hidden;
      width: 7.6rem;
      height: 7.6rem;
    }
    &__review-like-button {
      cursor: pointer;
      margin-top: 1.6rem;
      padding: 0.6rem 1.4rem;
      border-radius: 2.4rem;
      border: 0.1rem solid
        ${({
          theme: {
            color: { gray200 },
          },
        }) => gray200};
      background: transparent;
      display: flex;
      flex-direction: row;
      align-items: center;
      gap: 0.4rem;
      &--active {
        border-color: ${({
          theme: {
            color: { blue500 },
          },
        }) => blue500};
      }
    }
    &__more {
      margin: 0.8rem 0;
    }
    &__more-button {
      cursor: pointer;

      width: 100%;
      display: flex;
      flex-direction: row;
      align-items: center;
      justify-content: center;
      gap: 0.4rem;
      padding: 1.6rem;
      border-radius: 0.8rem;
      border: 0.1rem solid
        ${({
          theme: {
            color: { gray200 },
          },
        }) => gray200};
      background-color: transparent;
    }
  }
`

const ModalButton = styled.button`
  flex: 1;
  border-radius: 0.8rem;
  border: 0.1rem solid;
  padding: 1.3rem 1.6rem;
  cursor: pointer;
  width: 100%;

  &.ok {
    border-color: ${({ theme }) => theme.color.blue500};
    background-color: ${({ theme }) => theme.color.blue500};
  }

  &.cancel {
    border-color: ${({ theme }) => theme.color.gray200};
    background-color: ${({ theme }) => theme.color.grayWhite};
  }
`
